<script setup lang="ts">
/**
 * Переключатель языка.
 *
 * Ведёт на реальный адрес выбранной языковой версии, а не переключает
 * состояние на месте: у каждого языка свой URL, иначе им нельзя поделиться
 * и нечего индексировать.
 */
import { ref, onMounted, onBeforeUnmount } from 'vue'
import {
  useLanguage,
  ALL_LANGUAGES,
  LANGUAGE_NAMES,
  LANGUAGE_SHORT,
  type LanguageCode,
} from '@/composables/useLanguage'

const { current, pathFor, switchTo } = useLanguage()
const open = ref(false)
const root = ref<HTMLElement | null>(null)

function choose(language: LanguageCode): void {
  open.value = false
  if (language !== current.value) switchTo(language)
}

// Клик мимо и Esc закрывают меню — иначе оно висит поверх контента
function onDocumentClick(e: MouseEvent): void {
  if (root.value && !root.value.contains(e.target as Node)) open.value = false
}
function onKey(e: KeyboardEvent): void {
  if (e.key === 'Escape') open.value = false
}

onMounted(() => {
  document.addEventListener('click', onDocumentClick)
  document.addEventListener('keydown', onKey)
})
onBeforeUnmount(() => {
  document.removeEventListener('click', onDocumentClick)
  document.removeEventListener('keydown', onKey)
})
</script>

<template>
  <div ref="root" class="lang">
    <button
      type="button"
      class="btn btn-secondary btn-icon lang-toggle"
      :aria-label="LANGUAGE_NAMES[current]"
      aria-haspopup="listbox"
      :aria-expanded="open"
      @click="open = !open"
    >
      {{ LANGUAGE_SHORT[current] }}
    </button>

    <ul v-if="open" class="lang-menu" role="listbox">
      <li v-for="code in ALL_LANGUAGES" :key="code">
        <!-- Настоящая ссылка, а не кнопка: её видно краулеру, можно открыть
             в новой вкладке и скопировать адрес. -->
        <a
          :href="pathFor(code)"
          :hreflang="code"
          :lang="code"
          class="lang-item"
          :class="{ 'is-active': code === current }"
          role="option"
          :aria-selected="code === current"
          @click.prevent="choose(code)"
        >
          <span class="lang-code">{{ LANGUAGE_SHORT[code] }}</span>
          <span class="lang-name">{{ LANGUAGE_NAMES[code] }}</span>
        </a>
      </li>
    </ul>
  </div>
</template>

<style scoped>
.lang {
  position: relative;
}

.lang-toggle {
  font-family: var(--font-mono, monospace);
  font-size: 12px;
  letter-spacing: 0.06em;
}

.lang-menu {
  position: absolute;
  top: calc(100% + 4px);
  right: 0;
  z-index: 220;
  margin: 0;
  padding: 0;
  list-style: none;
  min-width: 150px;
  background: var(--color-bg);
  border: 2px solid var(--color-text);
}

.lang-item {
  display: flex;
  align-items: baseline;
  gap: var(--space-3);
  padding: var(--space-2) var(--space-3);
  color: inherit;
  text-decoration: none;
  font-size: 14px;
}

.lang-item:hover {
  background: var(--color-accent);
  color: var(--color-bg);
}

.lang-item.is-active {
  font-weight: 800;
}

.lang-item.is-active .lang-code {
  color: var(--color-accent);
}

.lang-item.is-active:hover .lang-code {
  color: var(--color-bg);
}

.lang-code {
  font-family: var(--font-mono, monospace);
  font-size: 11px;
  letter-spacing: 0.06em;
  width: 22px;
  flex: none;
}
</style>
